import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { endpoints } from '../config/apiConfig';

const Login = () => {
  const navigate = useNavigate();
  const [documento, setDocumento] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [error, setError] = useState('');
  const [sistemaDisponible, setSistemaDisponible] = useState(true);

  const formatFechaNacimientoParaApi = (fechaIso) => {
    if (!fechaIso) return '';
    const [yyyy, mm, dd] = fechaIso.split('-');
    if (!yyyy || !mm || !dd) return fechaIso;
    return `${yyyy}-${dd}-${mm}`;
  };

  useEffect(() => {
    const verificarEstadoSistema = async () => {
      try {
        const response = await fetch(endpoints.estadoSistema);
        const data = await response.json();

        if (!data.sistema_disponible) {
          setSistemaDisponible(false);
          toast.error(
            <div>
              <p className="font-bold">{data.mensaje}</p>
              <p className="text-sm mt-2">Tiempo restante: {data.tiempo_restante}</p>
              <p className="text-sm">Disponible hasta: {data.hora_fin}</p>
            </div>,
            {
              position: "bottom-right",
              autoClose: false,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: false,
            }
          );
        }
      } catch (error) {
        console.error('Error al verificar estado del sistema:', error);
      }
    };

    verificarEstadoSistema();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const documentoLimpio = documento.trim();

    if (!sistemaDisponible) {
      setError('El sistema no está disponible en este momento');
      return;
    }

    if (!documentoLimpio) {
      setError('Ingrese su número de documento');
      return;
    }

    if (!fechaNacimiento) {
      setError('Ingrese su fecha de nacimiento');
      return;
    }

    try {
      const parseJsonSafe = async (res) => {
        try {
          return await res.json();
        } catch {
          return null;
        }
      };

      const response = await fetch(endpoints.loginEstudiante, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          cedula: documentoLimpio,
          fecha_nacimiento: formatFechaNacimientoParaApi(fechaNacimiento)
        })
      });

      const data = await parseJsonSafe(response);

      if (!response.ok) {
        const message = data?.message || 'Error en la validación';
        throw new Error(message);
      }

      const existe = Boolean(data?.existe ?? data?.ok ?? data?.success);

      if (existe) {
        localStorage.setItem('documento', documentoLimpio);
        toast.success('Ha ingresado exitosamente', {
          position: 'bottom-right',
          autoClose: 2000
        });
        navigate('/verMatricula');
        return;
      }

      setError(data?.message || 'No fue posible validar los datos. Verifique e intente nuevamente.');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error al validar. Por favor, intente nuevamente.');
    }
  };

  return (
    <div className="bg-gray-200 min-h-screen flex items-center justify-center">
      <div className="bg-white shadow-md rounded-lg p-8 max-w-md w-full">
        <div className="flex justify-center mb-6">
          <img 
            src="http://sievi.udi.edu.co/sievi/img/logo-udi-negro.bbe5273e.png" 
            alt="Logo UDI" 
            className="h-12 w-auto"
          />
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Revisión de Matriculas
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="documento" className="block text-xs font-medium text-gray-700 mb-2 text-left">
              Número de documento
            </label>
            <input
              type="text"
              id="documento"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none ${
                !sistemaDisponible ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              required
              autoComplete="off"
              disabled={!sistemaDisponible}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="fechaNacimiento" className="block text-xs font-medium text-gray-700 mb-2 text-left">
              Fecha de nacimiento
            </label>
            <input
              type="date"
              id="fechaNacimiento"
              value={fechaNacimiento}
              onChange={(e) => setFechaNacimiento(e.target.value)}
              className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none ${
                !sistemaDisponible ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              required
              autoComplete="bday"
              disabled={!sistemaDisponible}
            />
          </div>
          {error && (
            <div className="mb-4 text-red-500 text-sm text-center">
              {error}
            </div>
          )}
          <button
            type="submit"
            className={`w-full px-4 py-2 rounded-md font-medium ${
              sistemaDisponible 
                ? 'bg-neutral-800 text-white hover:bg-neutral-700' 
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
            disabled={!sistemaDisponible}
          >
            Ingresar
          </button>
        </form>
        <div className="text-center mt-4">
          <a
            href={process.env.PUBLIC_URL + '/INSTRUCTIVO_REVISIÓN_DE_MATRICULA_ESTUDIANTE.pdf'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline text-sm"
          >
            Ver Instructivo
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
